import { createUpdatedFilterSearchParams } from '@/app/common/filters/searchParams/createUpdatedSearchParams.ts';
import { waitingCallGeneric } from '@/app/components/search/common/waitingCallGeneric.ts';
import type { DynamicFiltersAPIResponse } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import { buildFilterGroups } from '@/app/common/filters/groups/buildFilterGroups.ts';
import clientAPICall from '@/app/components/search/common/clientAPICall.ts';
import type { DynamicFiltersRequestPayload } from '@/app/api/search/dynamicfilters/createDynamicFiltersData.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import type { Filter, FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';

export type DynamicFiltersUpdateResult = {
  updatedGroups?: FiltersGroups;
  total?: number;
};

type UpdateDynamicFiltersProps = {
  filtersGroups: FiltersGroups;
  updatedFilters: Filter[];
  searchInfoData: SearchInfoData;
  productsData: ProductsData;
  commonData: CommonData;
  localization: Localization;
  translationDictionary: TranslationDictionary;
  // Only self-filtering filters may rebuild their own counters; the total is always wanted.
  rebuildGroups: boolean;
  isCancelled?: () => boolean;
  handleFiltersGroupsUpdated: (result: DynamicFiltersUpdateResult) => void;
};
export const updateDynamicFilters = ({
  filtersGroups,
  updatedFilters,
  searchInfoData,
  productsData,
  commonData,
  localization,
  translationDictionary,
  rebuildGroups,
  isCancelled,
  handleFiltersGroupsUpdated,
}: UpdateDynamicFiltersProps) => {
  const { authResponse } = commonData;
  const updatedSearchParams = createUpdatedFilterSearchParams({ filtersGroups, updatedFilters });

  const dynamicFiltersCall = () =>
    clientAPICall<DynamicFiltersRequestPayload, DynamicFiltersAPIResponse>({
      apiName: API_NAME.DYNAMIC_FILTERS,
      payload: {
        searchParams: updatedSearchParams,
        searchInfoData,
        commonData,
        localization,
      },
      traceParent: searchInfoData.searchTraceParent,
      commonData,
    });
  waitingCallGeneric<DynamicFiltersAPIResponse>({
    waitingAPICall: dynamicFiltersCall,
    isCancelled,
    handleWaitingCallFinished: (dynamicFiltersAPIResponse) => {
      const filters = dynamicFiltersAPIResponse?.response?.filters;
      const updatedGroups =
        rebuildGroups && filters
          ? buildFilterGroups({
              searchParams: updatedSearchParams,
              searchInfoData,
              loggedIn: authResponse.loggedIn,
              commonData,
              localization,
              productsData,
              dynamicFiltersResponse: filters,
              translationDictionary,
            })
          : undefined;

      handleFiltersGroupsUpdated({ updatedGroups, total: filters?.total });
    },
  });
};
